import { Type, inject } from '@angular/core';
import { BaseModel } from '@kinet-open-network/client/shared/data-access/model';
import { RawPermissions } from '@kinet-open-network/shared/data-access/model';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Observable, firstValueFrom } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import {
  CallState,
  setError,
  setLoaded,
  setLoading,
} from './call-state.feature';

export interface IPermissionsService<T extends BaseModel> {
  loadPermissions(id: T['id']): Observable<RawPermissions>;
  updatePermissions(
    id: T['id'],
    permissions: RawPermissions
  ): Observable<RawPermissions>;
}

export function withPermissions<T extends BaseModel>(
  Service: Type<IPermissionsService<T>>
) {
  return signalStoreFeature(
    {
      state: type<{
        callState: CallState;
      }>(),
    },
    withState({
      permissions: [] as RawPermissions,
    }),
    withMethods((store, service = inject(Service)) => ({
      async loadPermissions(id: T['id']) {
        patchState(store, setLoading());

        const permissions = await firstValueFrom(
          service.loadPermissions(id).pipe(
            tapResponse({
              next: (permissions) =>
                patchState(store, { permissions }, setLoaded()),
              error: (err) => patchState(store, setError(err)),
            })
          )
        );

        return permissions;
      },
      async updatePermissions(id: T['id'], permissions: RawPermissions) {
        patchState(store, setLoading());

        const updatedPermissions = await firstValueFrom(
          service.updatePermissions(id, permissions).pipe(
            tapResponse({
              next: (permissions) =>
                patchState(store, { permissions }, setLoaded()),
              error: (err) => patchState(store, setError(err)),
            })
          )
        );

        return updatedPermissions;
      },
    }))
  );
}
